import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { AuthContextType, LoginCredentials, User } from '../types';
import { authService, apiService } from '../services';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  // Handle auth failure from API service (e.g., refresh token expired)
  const handleAuthFailure = useCallback(() => {
    setUser(null);
    setToken(null);
    setMustChangePassword(false);
    setLoading(false);
    // Navigate using window.location to ensure full state reset
    window.location.href = '/login';
  }, []);

  // Handle token refresh from API service
  const handleTokenRefresh = useCallback((newToken: string) => {
    setToken(newToken);
  }, []);

  useEffect(() => {
    let isMounted = true;

    // Subscribe to API service events
    apiService.setOnTokenRefreshed(handleTokenRefresh);
    apiService.setOnAuthFailure(handleAuthFailure);

    const initializeAuth = async () => {
      const storedToken = authService.getToken();
      if (storedToken) {
        try {
          const userData = await authService.getCurrentUser();
          if (isMounted) {
            setUser(userData);
            setToken(storedToken);
            setMustChangePassword(userData.must_change_password ?? false);
          }
        } catch (error) {
          console.error('Failed to get current user:', error);
          if (isMounted) {
            // Token might be invalid, clear it
            await authService.logout();
          }
        }
      }
      if (isMounted) {
        setLoading(false);
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
    };
  }, [handleTokenRefresh, handleAuthFailure]);

  const login = async (credentials: LoginCredentials) => {
    setLoading(true);
    try {
      const response = await authService.login(credentials);
      setToken(response.access_token);
      setMustChangePassword(response.must_change_password ?? false);

      // If user data wasn't fetched during login, try to fetch it now
      if (response.user) {
        setUser(response.user);
      } else {
        try {
          const userData = await authService.getCurrentUser();
          setUser(userData);
        } catch (userError) {
          console.error('Failed to fetch user data after login:', userError);
          // Login still succeeded, user can be fetched later
        }
      }
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setToken(null);
      setMustChangePassword(false);
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    try {
      const userData = await authService.getCurrentUser();
      setUser(userData);
      setMustChangePassword(userData.must_change_password ?? false);
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    isAuthenticated: !!user && !!token,
    loading,
    mustChangePassword,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
